import React, { useEffect, useState } from 'react';
import moment from 'moment';
import classNames from 'classnames';
import { phone } from 'phone';
import { Form, getFormValues, reduxForm } from 'redux-form';
import { makeStyles } from '@material-ui/styles';
import { Box } from '@material-ui/core';

import { connectTo } from '../../../utils';

import {
  FormDateSelect,
  RadioGroup,
  Text,
  ThemeButton,
  Tooltip
} from '../../../components/common';

import rentedGoodsBox from '../../../assets/img/rented-goods/rented_goods_box.svg';
import { useAppWidthClass } from '../../../utils/hooks';
import { TIME_AFTERNOON, TIME_MORNING } from '../../../constants/return-goods';

import { ReturnGoodsContactDetails } from '../index';

const useStyles = makeStyles(theme => ({
  titleRow: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  },
  title: {
    marginLeft: theme.spacing(2)
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    margin: 'auto'
  },
  description: {
    marginTop: theme.spacing(4),
    textAlign: 'center'
  },
  dateLabel: {
    marginTop: theme.spacing(2)
  },
  section: {
    marginTop: theme.spacing(2)
  },
  tooltipMobile: {
    marginTop: theme.spacing(1)
  },
  tooltipMargin: {
    marginTop: theme.spacing(8)
  },
  dateWrapper: {
    display: 'inline-block'
  },
  dateMobile: {
    width: '100%',

    '& > div': {
      width: '100%',

      '& > div': {
        width: '100%'
      }
    }
  },
  dateSelect: {
    width: 205,
    display: 'flex',
    justifyContent: 'center',
    paddingRight: 0
  },
  radio: {
    marginLeft: 0,
    marginRight: 0
  },
  radioIcon: {
    padding: theme.spacing(0.5),

    '& svg:first-of-type': {
      transform: 'scale(0.8)'
    }
  },
  radioLabel: {
    marginLeft: theme.spacing(2)
  },
  line: {
    display: 'inline-block',
    margin: `${theme.spacing(1)}px 0`,
    width: '100%',
    height: 1,
    background: theme.palette.text.disabled
  },
  confirmationText: {
    paddingTop: theme.spacing(2),
    textAlign: 'center'
  },
  textLeft: {
    textAlign: 'left'
  },
  buttons: {
    marginTop: theme.spacing(2),
    display: 'flex',
    justifyContent: 'center',

    '& > button:first-of-type': {
      marginRight: theme.spacing(1)
    },
    '& > button:last-of-type': {
      marginLeft: theme.spacing(1)
    }
  },
  buttonMobile: {
    minWidth: 'unset',
    flexGrow: 1
  }
}));

const isDayValid = date => {
  return date != null && ![0, 6].includes(moment(date).day());
};

const ReturnGoodsEditViewContent = ({
  contentClass,
  handleSubmit,
  values,
  onConfirm,
  onDeclineButton,
  customerName,
  street,
  postCode,
  city,
  addressCountry,
  phone
}) => {
  const classes = useStyles();
  const { isDesktopWidth, isMobileWidth } = useAppWidthClass();

  const [initialDay] = useState(values.pickupDate);
  const [dayChanged, setDayChanged] = useState(false);

  useEffect(() => {
    if (initialDay != null && values.pickupDate !== initialDay) {
      setDayChanged(true);
    }
  }, [initialDay, values]);

  const dayError = !isDayValid(values.pickupDate);
  const showPickMessage = !dayChanged || dayError;

  return (
    <>
      <Box className={classes.titleRow}>
        <img src={rentedGoodsBox} alt="box" />
        <Text
          className={classes.title}
          component="h1"
          variant="h4"
          color="secondary"
        >
          Return of rented goods
        </Text>
      </Box>
      <Box className={classNames(classes.content, contentClass)}>
        <Text
          className={classNames(
            classes.description,
            !isDesktopWidth && classes.textLeft
          )}
          component="p"
          variant="body1"
          color="textPrimary"
        >
          The rental of your goods has expired. Please select your preferred
          date for our conveyance partner to pick up the items.
        </Text>
        <Form
          onSubmit={handleSubmit(({ pickupDate, time }) =>
            onConfirm(pickupDate, time)
          )}
        >
          <Text
            className={classes.dateLabel}
            component="h3"
            variant="h4"
            color="textPrimary"
          >
            Pickup date
          </Text>
          <Box className={classes.section}>
            <Tooltip
              open
              arrow
              secondary={showPickMessage}
              title={showPickMessage ? 'Please select a date!' : 'Done!'}
              placement={isMobileWidth ? 'bottom' : 'right'}
              tooltipClass={isMobileWidth ? classes.tooltipMobile : undefined}
            >
              <Box
                className={classNames(
                  classes.dateWrapper,
                  isMobileWidth && classes.dateMobile
                )}
              >
                <FormDateSelect
                  className={classes.dateSelect}
                  name="pickupDate"
                  minDate={moment().add(3, 'd')}
                  maxDate={moment().add(3, 'month')}
                  shouldDisableDateCheck={day => !isDayValid(day)}
                  hintMode={!dayChanged}
                  error={dayError}
                />
              </Box>
            </Tooltip>
          </Box>
          <Box
            className={classNames(
              classes.section,
              isMobileWidth && classes.tooltipMargin
            )}
          >
            <RadioGroup
              name="time"
              radioClassName={classes.radio}
              radioIconClassName={classes.radioIcon}
              radioLabelClassName={classes.radioLabel}
              options={[
                {
                  label: <Box>In the morning</Box>,
                  value: TIME_MORNING,
                  id: 'page.return-goods.edit-form.time-morning',
                  testLabelRole: 'return-time-item'
                },
                {
                  label: <Box>In the afternoon</Box>,
                  value: TIME_AFTERNOON,
                  id: 'page.return-goods.edit-form.time-other',
                  testLabelRole: 'return-time-item'
                }
              ]}
            />
          </Box>
          <Box className={classes.section}>
            <Box className={classes.line} />
          </Box>
          <ReturnGoodsContactDetails
            title="Personal details & pickup address"
            customerName={customerName}
            street={street}
            postCode={postCode}
            city={city}
            addressCountry={addressCountry}
            phone={phone}
          />
          <Box className={classes.section}>
            <Text
              className={classNames(
                classes.confirmationText,
                isMobileWidth && classes.textLeft
              )}
              component="p"
              variant="body1"
              color="textPrimary"
            >
              Are these details correct?
            </Text>
            <Box className={classes.buttons}>
              <ThemeButton
                className={isMobileWidth && classes.buttonMobile}
                id="page.return-goods.edit-form.cancel-button"
                color="secondary"
                variant="outlined"
                size="large"
                endIcon="dismissIcon"
                endIconColor="secondary"
                onClick={onDeclineButton}
                textKey={'No'}
              />
              <ThemeButton
                className={isMobileWidth && classes.buttonMobile}
                id="page.return-goods.edit-form.confirm-button"
                disabled={dayError}
                type="submit"
                variant="contained"
                size="large"
                endIcon="confirm"
                endIconColor="primaryContrast"
                textKey={'Yes'}
              />
            </Box>
          </Box>
        </Form>
      </Box>
    </>
  );
};

const FORM_NAME = 'return-goods';
const connected = connectTo(
  state => ({
    values: getFormValues(FORM_NAME)(state)
  }),
  {},
  ReturnGoodsEditViewContent
);

const getInitialValue = () => {
  const date = moment().add(14, 'd');

  // check and move over weekend
  if (date.day() === 0) {
    date.add(1, 'd');
  } else if (date.day() === 6) {
    date.add(2, 'd');
  }
  return date.valueOf();
};

const ReturnGoodsEditView = reduxForm({
  form: 'return-goods',
  initialValues: {
    pickupDate: getInitialValue(),
    time: TIME_MORNING
  }
})(connected);

export default ReturnGoodsEditView;
